import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class DateFormatterService {
  /**
   * Formats a date into YYYY-MM-DD format
   * @param date The date to format (can be string or Date object)
   * @returns Formatted date string in YYYY-MM-DD format
   */
  formatDate(date: string | Date): string {
    if (!date) return '';
    
    try {
      // If it's already a string in YYYY-MM-DD format, return as is
      if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
      }

      // Convert to Date object if it's a string
      const d = typeof date === 'string' ? new Date(date) : date;
      
      if (isNaN(d.getTime())) {
        console.error('Invalid date:', date);
        return '';
      }
      
      // Format as YYYY-MM-DD string
      const year = d.getFullYear().toString();
      const month = (d.getMonth() + 1).toString().padStart(2, '0');
      const day = d.getDate().toString().padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  }

  /**
   * Parses a date string in YYYY-MM-DD format to a Date object
   * @param dateString The date string in YYYY-MM-DD format
   * @returns Date object or null if invalid
   */
  parseDate(dateString: string): Date | null {
    if (!dateString) return null;

    try {
      // Check if the string is in YYYY-MM-DD format
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        console.error('Invalid date format. Expected YYYY-MM-DD');
        return null;
      }

      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day); // month is 0-based in JS Date

      // Validate the date
      if (isNaN(date.getTime())) {
        console.error('Invalid date:', dateString);
        return null;
      }

      return date;
    } catch (error) {
      console.error('Error parsing date:', error);
      return null;
    }
  }

  /**
   * Validates if a string is a valid date in YYYY-MM-DD format
   * @param dateString The date string to validate
   * @returns boolean indicating if the date is valid
   */
  isValidDate(dateString: string): boolean {
    if (!dateString) return false;
    
    const date = this.parseDate(dateString);
    return date !== null;
  }
} 